/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { fail } from './Common';
/**
 * Interns strings as integers.
 * Given a string, this class will produce a unique integer associated with that string that can then be used to retrieve the string.
 */
export class MutableStringInterner {
    /**
     * @param inputStrings - A list of strings to intern in the order given. Can be used to rehydrate from a previous
     * `StringInterner`'s {@link StringInterner.getSerializable} return value.
     */
    constructor(inputStrings = []) {
        this.stringToInternedIdMap = new Map();
        this.internedStrings = [];
        for (const value of inputStrings) {
            this.getOrCreateInternedId(value);
        }
    }
    /**
     * @param input - The string to get the associated intern ID for
     * @returns an intern ID that is uniquely associated with the input string
     */
    getOrCreateInternedId(input) {
        var _a;
        return (_a = this.getInternedId(input)) !== null && _a !== void 0 ? _a : this.createNewId(input);
    }
    getInternedId(input) {
        return this.stringToInternedIdMap.get(input);
    }
    /**
     *
     * @param internId - The intern ID to get the associated string for. Can only retrieve strings that have been
     * used as inputs to calls of `getInternId`.
     * @returns a string that is uniquely associated with the given intern ID
     */
    getString(internId) {
        var _a;
        return (_a = this.internedStrings[internId]) !== null && _a !== void 0 ? _a : fail(`No string associated with ${internId}.`);
    }
    /**
     * @returns The list of strings interned where the indices map to the associated {@link InternedStringId} of
     * each string.
     */
    getSerializable() {
        return this.internedStrings;
    }
    /** Create a new interned id. Assumes without validation that the input doesn't already have an interned id. */
    createNewId(input) {
        const internedId = this.stringToInternedIdMap.size;
        this.stringToInternedIdMap.set(input, internedId);
        this.internedStrings.push(input);
        return internedId;
    }
}
//# sourceMappingURL=StringInterner.js.map